import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Action from '../../components/Action'
import Icon from '../../components/Icon'

import { MARKER_MODES, ModuleMarkerIcon } from './ModuleMarker'

const LegendWrapper = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  color: ${(props) => props.theme.colors.text};
`

const LegendAction = styled(Action)`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  color: ${(props) => props.theme.colors.text};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`

const LegendPanel = styled.section`
  width: min(22rem, calc(100vw - 3rem));
  margin-top: 0.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`

const LegendTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
`

const LegendList = styled.dl`
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 0.5rem 0.75rem;
  align-items: center;
  margin: 0;
`

const MarkerExample = styled.span`
  display: inline-flex;
  width: 30px;
  height: 37px;
  align-items: center;
  justify-content: center;
`

const LegendDescription = styled.dd`
  margin: 0;
`

const CombinationHint = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
`

const examples = [
  { key: 'compact', displayMode: MARKER_MODES.COMPACT },
  { key: 'dot', displayMode: MARKER_MODES.DOT },
  { key: 'default' },
  { key: 'open', hasOpenValve: true },
  { key: 'suspended', hasSuspendedValve: true },
  { key: 'error', hasUnreadError: true },
  { key: 'selected', selected: true },
  {
    key: 'combined',
    hasOpenValve: true,
    hasSuspendedValve: true,
    hasUnreadError: true,
  },
]

const ModuleMarkerLegend = () => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const panelId = 'module-marker-legend'

  return (
    <LegendWrapper>
      <LegendAction
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('map:moduleMarkerLegend.toggle')}
      >
        <Icon name="info-circle" />
        {t('map:moduleMarkerLegend.button')}
      </LegendAction>
      {open && (
        <LegendPanel
          id={panelId}
          aria-label={t('map:moduleMarkerLegend.title')}
        >
          <LegendTitle>{t('map:moduleMarkerLegend.title')}</LegendTitle>
          <LegendList>
            {examples.map(({ key, ...markerProps }) => (
              <React.Fragment key={key}>
                <MarkerExample aria-hidden="true">
                  <ModuleMarkerIcon {...markerProps} />
                </MarkerExample>
                <LegendDescription>
                  {t(`map:moduleMarkerLegend.entries.${key}`)}
                </LegendDescription>
              </React.Fragment>
            ))}
          </LegendList>
          <CombinationHint>
            {t('map:moduleMarkerLegend.combinations')}
          </CombinationHint>
        </LegendPanel>
      )}
    </LegendWrapper>
  )
}

export default ModuleMarkerLegend
